import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useAdminOps } from '../contexts/AdminOpsContext';
import { OperationsPanel, SystemHealthPanel } from '../components/AdminOverlay';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield, Users, FileText, TrendingUp, CreditCard, Package, Search,
  Plus, Minus, Loader2, Check, X, Coins, BarChart3, Activity, Eye,
  ChevronRight, ArrowUpRight, ArrowDownRight, UserCheck, UserX,
  Clock, Zap, Brain, Settings2, RefreshCw, MoreVertical, Edit2, Trash2,
  AlertCircle
} from 'lucide-react';

// Stat card component
function StatCard({ icon: Icon, label, value, trend, trendLabel, color, bgColor }) {
  return (
    <Card className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 overflow-hidden group hover:border-white/10 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] text-[#6B7280] uppercase tracking-wider mb-2">{label}</p>
            <p className="text-3xl font-bold tabular-nums tracking-tight">{value}</p>
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-[11px] ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                <span className="font-medium">{trend >= 0 ? '+' : ''}{trend}</span>
                {trendLabel && <span className="text-[#6B7280]">{trendLabel}</span>}
              </div>
            )}
          </div>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bgColor || 'bg-white/[0.04]'}`}>
            <Icon size={18} className={color || 'text-[#D4AF37]'} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Activity item
function ActivityItem({ action, details, date, isTr }) {
  const actionLabels = {
    signup: { label: isTr ? 'Yeni kayıt' : 'New signup', icon: UserCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    post_published: { label: isTr ? 'Post yayınlandı' : 'Post published', icon: FileText, color: 'text-sky-400', bg: 'bg-sky-500/10' },
    post_created: { label: isTr ? 'Post oluşturuldu' : 'Post created', icon: FileText, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    ai_content_generated: { label: isTr ? 'AI içerik' : 'AI content', icon: Brain, color: 'text-purple-400', bg: 'bg-purple-500/10' },
    channel_connected: { label: isTr ? 'Kanal bağlandı' : 'Channel connected', icon: Zap, color: 'text-[#00B8A9]', bg: 'bg-[#00B8A9]/10' },
    credits_adjusted: { label: isTr ? 'Kredi ayarlandı' : 'Credits adjusted', icon: Coins, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    plan_upgraded: { label: isTr ? 'Plan yükseltildi' : 'Plan upgraded', icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    image_generated: { label: isTr ? 'Görsel üretildi' : 'Image generated', icon: Brain, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    brand_created: { label: isTr ? 'Marka oluşturuldu' : 'Brand created', icon: Package, color: 'text-[#00B8A9]', bg: 'bg-[#00B8A9]/10' },
  };
  const cfg = actionLabels[action] || { label: action, icon: Activity, color: 'text-[#6B7280]', bg: 'bg-white/5' };
  const Icon = cfg.icon;

  return (
    <div className="flex items-center gap-3 py-2.5 group">
      <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
        <Icon size={14} className={cfg.color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-medium truncate">{cfg.label}</p>
        <p className="text-[10px] text-[#6B7280] truncate">
          {details?.email || details?.channel || details?.brand_name || details?.post_id?.slice(0,8) || ''}
        </p>
      </div>
      <span className="text-[10px] text-[#4B5563] shrink-0 tabular-nums">
        {new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
}

export default function AdminPage() {
  const { i18n } = useTranslation();
  const { api } = useAuth();
  const { opsData } = useAdminOps();
  const isTr = i18n.language?.startsWith('tr');

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [packages, setPackages] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Dialogs
  const [creditDialog, setCreditDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState(0);
  const [creditReason, setCreditReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsR, usersR, pkgR, actR] = await Promise.all([
        api.get('/api/admin/stats'),
        api.get('/api/admin/users', { params: { limit: 50, search: searchQuery || undefined } }),
        api.get('/api/packages'),
        api.get('/api/activity', { params: { limit: 20 } }),
      ]);
      setStats(statsR.data);
      setUsers(usersR.data.users || []);
      setUsersTotal(usersR.data.total || 0);
      setPackages(pkgR.data);
      setRecentActivity(actR.data.activities || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error(isTr ? 'Admin yetkisi gerekli' : 'Admin access required');
    } finally { setLoading(false); }
  }, [api, searchQuery, isTr]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSearch = () => loadData();

  const openCreditDialog = (u) => { setSelectedUser(u); setCreditAmount(0); setCreditReason(''); setCreditDialog(true); };
  const openRoleDialog = (u) => { setSelectedUser(u); setNewRole(u.role === 'admin' ? 'user' : 'admin'); setRoleDialog(true); };

  const handleAdjustCredits = async () => {
    if (!selectedUser || !creditAmount || !creditReason.trim()) return;
    setAdjusting(true);
    try {
      await api.post('/api/admin/credits/adjust', { user_id: selectedUser.id, amount: creditAmount, reason: creditReason });
      toast.success(isTr ? `${Math.abs(creditAmount)} kredi ${creditAmount > 0 ? 'eklendi' : 'çıkarıldı'}` : `${Math.abs(creditAmount)} credits ${creditAmount > 0 ? 'added' : 'deducted'}`);
      setCreditDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAdjusting(false); }
  };

  const handleRoleChange = async () => {
    if (!selectedUser) return;
    try {
      await api.put(`/api/admin/users/${selectedUser.id}/role?role=${newRole}`);
      toast.success(isTr ? `Rol güncellendi: ${newRole}` : `Role updated: ${newRole}`);
      setRoleDialog(false);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const tabs = [
    { id: 'overview', icon: BarChart3, label: isTr ? 'Genel Bakış' : 'Overview' },
    { id: 'users', icon: Users, label: isTr ? 'Kullanıcılar' : 'Users' },
    { id: 'packages', icon: Package, label: isTr ? 'Paketler' : 'Packages' },
    { id: 'operations', icon: Activity, label: isTr ? 'Operasyonlar' : 'Operations' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#00B8A9]/20 flex items-center justify-center border border-white/[0.06]">
            <Shield size={20} className="text-[#D4AF37]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {isTr ? 'Yönetici Paneli' : 'Admin Panel'}
            </h1>
            <p className="text-[11px] text-[#6B7280]">{isTr ? 'Sistem yönetimi ve izleme' : 'System management & monitoring'}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={loadData} className="text-[#6B7280] hover:text-white gap-1.5">
          <RefreshCw size={13} /> {isTr ? 'Yenile' : 'Refresh'}
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 p-1 rounded-xl bg-white/[0.02] border border-white/[0.04] w-fit">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/15'
                : 'text-[#6B7280] hover:text-white hover:bg-white/[0.04]'
            }`}>
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* =========== OVERVIEW TAB =========== */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label={isTr ? 'Toplam Kullanıcı' : 'Total Users'}
              value={stats?.total_users || 0} trend={stats?.recent_signups} trendLabel={isTr ? 'bu hafta' : 'this week'}
              color="text-[#D4AF37]" bgColor="bg-[#D4AF37]/10" />
            <StatCard icon={FileText} label={isTr ? 'Toplam Gönderi' : 'Total Posts'}
              value={stats?.total_posts || 0}
              color="text-[#00B8A9]" bgColor="bg-[#00B8A9]/10" />
            <StatCard icon={Check} label={isTr ? 'Yayınlanan' : 'Published'}
              value={stats?.published_posts || 0}
              color="text-emerald-400" bgColor="bg-emerald-500/10" />
            <StatCard icon={Clock} label={isTr ? 'Planlanan' : 'Scheduled'}
              value={stats?.scheduled_posts || 0}
              color="text-sky-400" bgColor="bg-sky-500/10" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Recent Activity */}
            <div className="lg:col-span-7">
              <Card className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Activity size={14} className="text-[#00B8A9]" />
                      {isTr ? 'Son Aktiviteler' : 'Recent Activity'}
                    </h3>
                    <Badge variant="outline" className="text-[9px] border-white/10">{recentActivity.length}</Badge>
                  </div>
                  <div className="divide-y divide-white/[0.03]">
                    {recentActivity.slice(0, 10).map((act, i) => (
                      <ActivityItem key={i} action={act.action} details={act.details} date={act.created_at} isTr={isTr} />
                    ))}
                    {recentActivity.length === 0 && (
                      <p className="text-[12px] text-[#6B7280] text-center py-8">{isTr ? 'Henüz aktivite yok' : 'No activity yet'}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats + Recent Users */}
            <div className="lg:col-span-5 space-y-4">
              {/* Media stats */}
              <Card className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60">
                <CardContent className="p-5 space-y-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Brain size={14} className="text-[#D4AF37]" />
                    {isTr ? 'AI & Medya' : 'AI & Media'}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                      <p className="text-2xl font-bold tabular-nums">{stats?.total_media || 0}</p>
                      <p className="text-[10px] text-[#6B7280]">{isTr ? 'Medya Varlığı' : 'Media Assets'}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                      <p className="text-2xl font-bold tabular-nums">{stats?.recent_signups || 0}</p>
                      <p className="text-[10px] text-[#6B7280]">{isTr ? '7 Gün Kayıt' : '7-Day Signups'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Top Users */}
              <Card className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60">
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Users size={14} className="text-[#00B8A9]" />
                      {isTr ? 'Son Kullanıcılar' : 'Recent Users'}
                    </h3>
                    <button onClick={() => setActiveTab('users')} className="text-[10px] text-[#D4AF37] hover:underline flex items-center gap-0.5">
                      {isTr ? 'Tümü' : 'All'} <ChevronRight size={10} />
                    </button>
                  </div>
                  {users.slice(0, 5).map((u, i) => (
                    <div key={i} className="flex items-center gap-3 py-1.5">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#00B8A9]/20 flex items-center justify-center text-[10px] font-bold border border-white/[0.06]">
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-medium truncate">{u.name}</p>
                        <p className="text-[10px] text-[#6B7280] truncate">{u.email}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-[11px] tabular-nums font-medium">{u.credits}</span>
                        <p className="text-[9px] text-[#6B7280]">{isTr ? 'kredi' : 'cr'}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* =========== USERS TAB =========== */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder={isTr ? 'İsim veya e-posta ile ara...' : 'Search by name or email...'}
                className="pl-9 h-10 bg-[#0D1117] border-white/[0.06] text-sm rounded-xl" data-testid="admin-user-search" />
            </div>
            <Button variant="outline" size="sm" onClick={handleSearch} className="border-white/[0.06] rounded-xl h-10 px-4">
              <Search size={14} />
            </Button>
            <Badge variant="outline" className="border-white/[0.06] text-[11px]">{usersTotal} {isTr ? 'kullanıcı' : 'users'}</Badge>
          </div>

          {/* Users List */}
          <Card className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-users-table">
                <thead>
                  <tr className="border-b border-white/[0.04]">
                    <th className="text-left text-[10px] text-[#6B7280] uppercase tracking-wider px-5 py-3 font-medium">{isTr ? 'Kullanıcı' : 'User'}</th>
                    <th className="text-left text-[10px] text-[#6B7280] uppercase tracking-wider px-5 py-3 font-medium hidden sm:table-cell">{isTr ? 'Plan' : 'Plan'}</th>
                    <th className="text-right text-[10px] text-[#6B7280] uppercase tracking-wider px-5 py-3 font-medium">{isTr ? 'Kredi' : 'Credits'}</th>
                    <th className="text-center text-[10px] text-[#6B7280] uppercase tracking-wider px-5 py-3 font-medium hidden md:table-cell">{isTr ? 'Rol' : 'Role'}</th>
                    <th className="text-right text-[10px] text-[#6B7280] uppercase tracking-wider px-5 py-3 font-medium">{isTr ? 'İşlem' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  {users.map((u, i) => (
                    <tr key={u.id || i} className="hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${i}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37]/20 to-[#00B8A9]/20 flex items-center justify-center text-[11px] font-bold border border-white/[0.06] shrink-0">
                            {u.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium truncate">{u.name}</p>
                            <p className="text-[11px] text-[#6B7280] truncate">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell">
                        <Badge className={`text-[9px] border ${
                          u.plan === 'agency' ? 'bg-[#00B8A9]/10 text-[#00B8A9] border-[#00B8A9]/20'
                          : u.plan === 'pro' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20'
                          : 'bg-white/5 text-[#6B7280] border-white/10'
                        }`}>
                          {(u.plan || 'starter').charAt(0).toUpperCase() + (u.plan || 'starter').slice(1)}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <span className="text-[13px] font-semibold tabular-nums">{u.credits}</span>
                      </td>
                      <td className="px-5 py-3.5 text-center hidden md:table-cell">
                        <Badge className={`text-[9px] border ${
                          u.role === 'admin' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20' : 'bg-white/5 text-[#6B7280] border-white/10'
                        }`}>
                          {u.role === 'admin' ? (
                            <><Shield size={9} className="mr-0.5" /> Admin</>
                          ) : 'User'}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#D4AF37] hover:bg-[#D4AF37]/10 gap-1 rounded-lg"
                            onClick={() => openCreditDialog(u)} data-testid={`adjust-credits-${i}`}>
                            <Coins size={11} /> {isTr ? 'Kredi' : 'Credits'}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 text-[10px] text-[#00B8A9] hover:bg-[#00B8A9]/10 gap-1 rounded-lg"
                            onClick={() => openRoleDialog(u)} data-testid={`change-role-${i}`}>
                            <Shield size={11} /> {isTr ? 'Rol' : 'Role'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <div className="text-center py-12">
                <Users size={32} className="mx-auto mb-3 text-[#4B5563]" />
                <p className="text-[12px] text-[#6B7280]">{isTr ? 'Kullanıcı bulunamadı' : 'No users found'}</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* =========== PACKAGES TAB =========== */}
      {activeTab === 'packages' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#6B7280]">{packages.length} {isTr ? 'paket' : 'packages'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {packages.map((pkg, i) => (
              <Card key={pkg.id || i} className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60 overflow-hidden group hover:border-white/10 transition-colors">
                <CardContent className="p-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{pkg.name}</h3>
                    <Badge variant="outline" className="text-[10px] border-white/10 tabular-nums">${pkg.price}</Badge>
                  </div>
                  {pkg.description && <p className="text-[12px] text-[#6B7280]">{pkg.description}</p>}
                  <div className="flex items-center gap-2">
                    <Coins size={14} className="text-[#D4AF37]" />
                    <span className="text-sm font-semibold tabular-nums">{pkg.credits}</span>
                    <span className="text-[11px] text-[#6B7280]">{isTr ? 'kredi' : 'credits'}</span>
                  </div>
                  {pkg.features && pkg.features.length > 0 && (
                    <div className="space-y-1.5 pt-2 border-t border-white/[0.04]">
                      {pkg.features.map((f, fi) => (
                        <div key={fi} className="flex items-center gap-2 text-[11px] text-[#8B95A5]">
                          <Check size={11} className="text-[#00B8A9] shrink-0" /> {f}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* =========== OPERATIONS TAB =========== */}
      {activeTab === 'operations' && (
        <div className="space-y-5">
          <SystemHealthPanel />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60">
              <CardContent className="p-5">
                <OperationsPanel />
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-white/[0.06] bg-[#0D1117]/60">
              <CardContent className="p-5 space-y-4">
                <h4 className="text-xs font-semibold text-[#A9B4C0] flex items-center gap-1.5">
                  <Clock size={12} className="text-sky-400" /> {isTr ? 'Zamanlanmış İşler' : 'Scheduled Jobs'} ({opsData?.pending_scheduled?.length || 0})
                </h4>
                {(opsData?.pending_scheduled || []).length > 0 ? (
                  <div className="space-y-2">
                    {opsData.pending_scheduled.slice(0, 8).map((job, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                        <Clock size={12} className="text-sky-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] truncate">{job.post_content || job.post_id?.slice(0,8)}</p>
                          <p className="text-[9px] text-[#4B5563]">{job.scheduled_at ? new Date(job.scheduled_at).toLocaleString() : ''}</p>
                        </div>
                        <Badge variant="outline" className="text-[8px] border-sky-500/20 text-sky-300">scheduled</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-[#4B5563] text-center py-4">{isTr ? 'Zamanlanmış iş yok' : 'No scheduled jobs'}</p>
                )}

                <h4 className="text-xs font-semibold text-[#A9B4C0] flex items-center gap-1.5 pt-2">
                  <Zap size={12} className="text-[#00B8A9]" /> {isTr ? 'Kanal Bağlantıları' : 'Channel Connections'}
                </h4>
                {(opsData?.channel_connections || []).map((ch, i) => (
                  <div key={i} className="flex items-center justify-between p-2">
                    <span className="text-[11px] capitalize">{ch.channel}</span>
                    <span className="text-[11px] font-bold tabular-nums text-[#00B8A9]">{ch.count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* =========== CREDIT ADJUSTMENT DIALOG =========== */}
      <Dialog open={creditDialog} onOpenChange={setCreditDialog}>
        <DialogContent className="bg-[#0D1117] border-white/[0.08] rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Coins size={18} className="text-[#D4AF37]" />
              {isTr ? 'Kredi Ayarla' : 'Adjust Credits'} — {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] flex items-center justify-between">
              <span className="text-[12px] text-[#6B7280]">{isTr ? 'Mevcut bakiye' : 'Current balance'}</span>
              <span className="text-lg font-bold tabular-nums">{selectedUser?.credits}</span>
            </div>

            <div>
              <label className="text-[11px] text-[#6B7280] mb-2 block">{isTr ? 'Miktar' : 'Amount'}</label>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setCreditAmount(p => p - 50)}
                  className="border-white/[0.06] rounded-xl w-10 h-10 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                  <Minus size={14} />
                </Button>
                <Input type="number" value={creditAmount} onChange={e => setCreditAmount(parseInt(e.target.value) || 0)}
                  className="bg-black/20 border-white/[0.06] text-center text-lg font-bold tabular-nums rounded-xl h-10" data-testid="credit-amount-input" />
                <Button variant="outline" size="icon" onClick={() => setCreditAmount(p => p + 50)}
                  className="border-white/[0.06] rounded-xl w-10 h-10 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300">
                  <Plus size={14} />
                </Button>
              </div>
              {creditAmount !== 0 && (
                <p className={`text-[11px] mt-2 ${creditAmount > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isTr ? 'Yeni bakiye:' : 'New balance:'} {(selectedUser?.credits || 0) + creditAmount}
                </p>
              )}
            </div>

            <div>
              <label className="text-[11px] text-[#6B7280] mb-2 block">{isTr ? 'Sebep' : 'Reason'}</label>
              <Input value={creditReason} onChange={e => setCreditReason(e.target.value)}
                placeholder={isTr ? 'Kredi ayarlama sebebi...' : 'Reason for adjustment...'}
                className="bg-black/20 border-white/[0.06] text-sm rounded-xl h-10" data-testid="credit-reason-input" />
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={handleAdjustCredits} disabled={adjusting || !creditAmount || !creditReason.trim()}
                className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A0C10] font-semibold text-sm" data-testid="confirm-adjust-btn">
                {adjusting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Check size={14} className="mr-2" />}
                {isTr ? 'Onayla' : 'Confirm'}
              </Button>
              <Button variant="ghost" onClick={() => setCreditDialog(false)} className="h-11 rounded-xl text-[#6B7280] hover:text-white">
                {isTr ? 'İptal' : 'Cancel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* =========== ROLE CHANGE DIALOG =========== */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent className="bg-[#0D1117] border-white/[0.08] rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Shield size={18} className="text-[#00B8A9]" />
              {isTr ? 'Rol Değiştir' : 'Change Role'} — {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04] space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-[#6B7280]">{isTr ? 'Mevcut rol' : 'Current role'}</span>
                <Badge className={`text-[10px] border ${selectedUser?.role === 'admin' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20' : 'bg-white/5 text-[#6B7280] border-white/10'}`}>
                  {selectedUser?.role}
                </Badge>
              </div>
              <div className="flex items-center justify-center">
                <ArrowDownRight size={16} className="text-[#6B7280]" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] text-[#6B7280]">{isTr ? 'Yeni rol' : 'New role'}</span>
                <Badge className={`text-[10px] border ${newRole === 'admin' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/20' : 'bg-white/5 text-[#6B7280] border-white/10'}`}>
                  {newRole}
                </Badge>
              </div>
            </div>

            {newRole === 'admin' && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/5 border border-amber-500/15">
                <AlertCircle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                <p className="text-[11px] text-amber-300/80">
                  {isTr ? 'Admin kullanıcılar tüm sisteme erişebilir.' : 'Admin users can access the entire system.'}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleRoleChange}
                className="flex-1 h-11 rounded-xl bg-[#00B8A9] text-white font-semibold text-sm" data-testid="confirm-role-btn">
                <Check size={14} className="mr-2" /> {isTr ? 'Onayla' : 'Confirm'}
              </Button>
              <Button variant="ghost" onClick={() => setRoleDialog(false)} className="h-11 rounded-xl text-[#6B7280] hover:text-white">
                {isTr ? 'İptal' : 'Cancel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
